import React, { useState } from "react";
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, TextField, Icon } from "@fluentui/react";

export interface MessageFeedbackData {
  action: boolean | null; // true = like, false = dislike
  whatWentWell: string;
  whatDidNotGoWell: string;
  goodToHave: string;
}

export interface MessageFeedbackDialogProps {
  isOpen: boolean;
  reactionType: boolean | null; // true = like, false = dislike
  onDismiss: () => void;
  onSubmit: (feedback: MessageFeedbackData) => Promise<void>;
  isSubmitting: boolean;
}

const MessageFeedbackDialog: React.FC<MessageFeedbackDialogProps> = ({
  isOpen,
  reactionType,
  onDismiss,
  onSubmit,
  isSubmitting
}) => {
  const [whatWentWell, setWhatWentWell] = useState("");
  const [whatDidNotGoWell, setWhatDidNotGoWell] = useState("");
  const [goodToHave, setGoodToHave] = useState("");

  // Get logged-in user's email from session storage
  const userEmail = sessionStorage.getItem("loginUserName")?.toString() || "your email";

  const handleDismiss = () => {
    setWhatWentWell("");
    setWhatDidNotGoWell("");
    setGoodToHave("");
    onDismiss();
  };

  const handleSubmit = async () => {
    await onSubmit({
      action: reactionType,
      whatWentWell: whatWentWell.trim(),
      whatDidNotGoWell: whatDidNotGoWell.trim(),
      goodToHave: goodToHave.trim()
    });
    // Reset state after successful submit
    setWhatWentWell("");
    setWhatDidNotGoWell("");
    setGoodToHave("");
  };

  const isLike = reactionType === true;
  const headerIcon = isLike ? "Like" : "Dislike";
  const headerColor = isLike ? "#107c10" : "#d13438";
  const headerText = isLike
    ? "We're glad you liked this response!"
    : "We're sorry this response didn't meet your expectations!";

  const textFieldStyles = {
    root: {
      marginBottom: "2px"
    },
    fieldGroup: {
      borderRadius: "8px",
      border: "1px solid #e1dfdd",
      minHeight: "60px",
      transition: "all 0.2s ease",
      backgroundColor: "#fafafa",
      boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.04)",
      selectors: {
        ":hover": {
          borderColor: "#0078d4",
          backgroundColor: "#fff",
          boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.06)"
        },
        ":focus-within": {
          borderColor: "#0078d4",
          backgroundColor: "#fff",
          boxShadow: "0 0 0 2px rgba(0, 120, 212, 0.15)"
        }
      }
    },
    field: {
      fontSize: "13px",
      padding: "10px 12px",
      lineHeight: "1.5",
      color: "#323130"
    }
  };

  const labelStyles: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#323130",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  };

  const getLabelIcon = (type: string) => {
    switch (type) {
      case "good":
        return "Emoji2";
      case "bad":
        return "Sad";
      case "suggest":
        return "Lightbulb";
      default:
        return "";
    }
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        showCloseButton: true,
        styles: {
          inner: {
            padding: "0",
            overflow: "hidden"
          },
          innerContent: {
            padding: "0",
            overflow: "hidden"
          }
        }
      }}
      minWidth={520}
      maxWidth={520}
      modalProps={{
        isBlocking: false,
        styles: {
          main: {
            borderRadius: "16px !important",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 12px 24px -8px rgba(0, 0, 0, 0.15) !important",
            width: "520px !important",
            maxWidth: "520px !important",
            minWidth: "520px !important",
            overflow: "hidden"
          },
          scrollableContent: {
            overflow: "hidden"
          }
        }
      }}
      styles={{
        main: {
          borderRadius: "16px",
          overflow: "hidden",
          width: "520px !important",
          maxWidth: "520px !important",
          minWidth: "520px !important"
        }
      }}
    >
      {/* Custom Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "20px 24px 16px 50px",
          borderBottom: "1px solid #f3f2f1",
          margin: "0px -24px 0 -24px",
          background: isLike
            ? "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)"
            : "linear-gradient(135deg, #ffebee 0%, #ffcdd2 50%, #ef9a9a 100%)"
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `3px solid ${headerColor}`,
            boxShadow: `0 4px 12px ${isLike ? "rgba(16, 124, 16, 0.25)" : "rgba(209, 52, 56, 0.25)"}`,
            flexShrink: 0
          }}
        >
          <Icon
            iconName={headerIcon}
            style={{
              fontSize: "22px",
              color: headerColor
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#1a1a1a",
              marginBottom: "4px",
              letterSpacing: "-0.3px"
            }}
          >
            Share Your Feedback
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#424242",
              fontWeight: 500
            }}
          >
            {headerText}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div
        style={{
          padding: "20px 24px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "14px"
        }}
      >
        {/* What went well */}
        <div>
          <label style={labelStyles}>
            <Icon iconName={getLabelIcon("good")} style={{ fontSize: "16px", color: "#107c10" }} />
            What went well
          </label>
          <TextField
            placeholder="Tell us what you liked about this response..."
            multiline
            rows={2}
            value={whatWentWell}
            onChange={(_, newValue) => setWhatWentWell(newValue || "")}
            styles={textFieldStyles}
            resizable={false}
          />
        </div>

        {/* What did not go well */}
        <div>
          <label style={labelStyles}>
            <Icon iconName={getLabelIcon("bad")} style={{ fontSize: "16px", color: "#d13438" }} />
            What did not go well
          </label>
          <TextField
            placeholder="Share what could be improved..."
            multiline
            rows={2}
            value={whatDidNotGoWell}
            onChange={(_, newValue) => setWhatDidNotGoWell(newValue || "")}
            styles={textFieldStyles}
            resizable={false}
          />
        </div>

        {/* Good to have */}
        <div>
          <label style={labelStyles}>
            <Icon iconName={getLabelIcon("suggest")} style={{ fontSize: "16px", color: "#ffaa00" }} />
            Good to have
          </label>
          <TextField
            placeholder="Any suggestions or features you'd like to see..."
            multiline
            rows={2}
            value={goodToHave}
            onChange={(_, newValue) => setGoodToHave(newValue || "")}
            styles={textFieldStyles}
            resizable={false}
          />
        </div>

        {/* Disclaimer */}
        <div
          style={{
            padding: "12px 16px",
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            borderRadius: "10px",
            border: "1px solid #e1dfdd",
            marginTop: "4px"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px"
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                backgroundColor: "#e7f3ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <Icon
                iconName="Info"
                style={{
                  fontSize: "12px",
                  color: "#0078d4"
                }}
              />
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#605e5c",
                lineHeight: "1.5"
              }}
            >
              <span style={{ fontWeight: 600, color: "#0078d4" }}>Security360 Team</span> may contact you at{" "}
              <span style={{ color: "#0078d4", textDecoration: "underline" }}>{userEmail}</span> about your feedback.
              Learn more about how this data is used and your rights. By pressing Submit, your feedback will be used to
              improve Security360 AI Assistant and services.
            </div>
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <DialogFooter
        styles={{
          actions: {
            marginTop: "8px",
            padding: "0 24px 16px 24px"
          },
          actionsRight: {
            marginRight: "35px"
          }
        }}
      >
        <DefaultButton
          onClick={handleDismiss}
          text="Cancel"
          styles={{
            root: {
              minWidth: "100px",
              height: "36px",
              borderRadius: "6px",
              border: "1px solid #d2d0ce",
              backgroundColor: "#fff",
              transition: "all 0.2s ease"
            },
            rootHovered: {
              backgroundColor: "#f3f2f1",
              borderColor: "#8a8886"
            },
            label: {
              fontSize: "14px",
              fontWeight: 600,
              color: "#323130"
            }
          }}
        />
        <PrimaryButton
          onClick={handleSubmit}
          text="Submit feedback"
          disabled={isSubmitting}
          styles={{
            root: {
              minWidth: "140px",
              height: "36px",
              borderRadius: "6px",
              background: "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(0, 120, 212, 0.35)",
              transition: "all 0.2s ease"
            },
            rootHovered: {
              background: "linear-gradient(135deg, #106ebe 0%, #005a9e 100%)",
              boxShadow: "0 6px 16px rgba(0, 120, 212, 0.45)",
              transform: "translateY(-1px)"
            },
            rootPressed: {
              background: "linear-gradient(135deg, #005a9e 0%, #004578 100%)",
              boxShadow: "0 2px 8px rgba(0, 120, 212, 0.25)",
              transform: "translateY(0)"
            },
            label: {
              fontSize: "14px",
              fontWeight: 600
            }
          }}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default MessageFeedbackDialog;
